<script>
import PopupModal from '../PopupModal.vue'
import RepairFileService from '@/services/RepairFileService'
import { auth as $store } from '@/store/AuthModule'
import { handleResponseError } from '@/components/utils/globalFunction'
import Validator from '@/components/utils/validator'
import DialogCloseButton from '@/components/modal/DialogCloseButton.vue'
import DialogBackButton from '@/components/modal/DialogBackButton.vue'
import QrcodeVue from 'qrcode.vue'

export default {
  name: 'UploadAdditionalAttachmentModal',
  components: { QrcodeVue, DialogBackButton, DialogCloseButton, PopupModal },
  data: () => ({
    loading: false,

    repairFileId: undefined,
    resolvePromise: undefined,
    rejectPromise: undefined,

    qrCodeToken: null,
    qrCodeValue: null,
    qrCodeSize: 200,

    file: '',
    fileName: '',
    requiredRules: Validator.requiredRules,

    showAddQrCodeAttachment: false,
    showAddPcAttachment: false
  }),
  computed: {
    currentCollaborator() {
      return $store.state.currentCollaborator
    }
  },
  methods: {
    show(opts = {}) {
      this.repairFileId = opts.repairFileId

      this.generateAttachmentQrCode()

      this.$refs.popup.open()
      return new Promise((resolve, reject) => {
        this.resolvePromise = resolve
        this.rejectPromise = reject
      })
    },
    async _confirm() {
      this.loading = true
      const formData = new FormData()
      let data = {
        repair_file_id: this.repairFileId,
        file_name: this.fileName,
        requestor_collaborator_id: this.currentCollaborator.technical_id
      }
      formData.append('data', JSON.stringify(data))
      formData.append('additional_attachment', this.file)
      try {
        await RepairFileService.uploadAdditionalAttachmentFile(formData)
        this.$refs.popup.close()
        this.resolvePromise({
          confirmed: true
        })
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
        this.resetData()
      }
    },
    _cancel() {
      this.resetData()
      this.$refs.popup.close()
      this.resolvePromise(false)
    },
    handleBack() {
      this.showAddQrCodeAttachment = false
      this.showAddPcAttachment = false
    },
    resetData() {
      this.title = undefined
      this.file = ''
      this.fileName = ''
      this.showAddQrCodeAttachment = false
      this.showAddPcAttachment = false
    },
    async generateAttachmentQrCode() {
      try {
        this.qrCodeToken = await RepairFileService.getUploadAdditionalAttachmentQRCodeToken(
          this.repairFileId,
          this.currentCollaborator.technical_id
        )

        this.qrCodeValue =
          window.location.origin +
          '/public/repair-file/upload-additional-attachment?token=' +
          this.qrCodeToken.token
      } catch (error) {
        handleResponseError(error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<template>
  <popup-modal ref="popup">
    <div>
      <DialogCloseButton :close-method="_cancel" />
      <DialogBackButton
        v-if="showAddQrCodeAttachment || showAddPcAttachment"
        :back-method="handleBack"
      />

      <h2 class="font-weight-bold text-center mb-5">
        <span v-if="showAddQrCodeAttachment">
          {{ $t('REPAIR_FILE_ATTACHMENT.MODAL.QR_CODE_TITLE') }}
        </span>
        <span v-if="showAddPcAttachment">
          {{ $t('REPAIR_FILE_ATTACHMENT.MODAL.PC_TITLE') }}
        </span>
        <span v-if="!showAddQrCodeAttachment && !showAddPcAttachment">
          {{ $t('REPAIR_FILE_ATTACHMENT.MODAL.GENERIC_TITLE') }}
        </span>
      </h2>

      <v-row v-if="!showAddQrCodeAttachment && !showAddPcAttachment">
        <v-col cols="12">
          <h3 class="font-weight-medium">
            {{ $t('REPAIR_FILE_ATTACHMENT.MODAL.GENERIC_SUBTITLE') }}
          </h3>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$t('REPAIR_FILE_ATTACHMENT.MODAL.INPUT_LABEL_BY_QR_CODE')"
            append-inner-icon="mdi-camera"
            @click="showAddQrCodeAttachment = true"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            :label="$t('REPAIR_FILE_ATTACHMENT.MODAL.INPUT_LABEL_BY_PC')"
            append-inner-icon="mdi-download"
            @click="showAddPcAttachment = true"
          />
        </v-col>
      </v-row>

      <v-row v-if="showAddQrCodeAttachment">
        <v-col cols="12">
          <h3 class="font-weight-medium">
            {{ $t('ATTACHMENT_MODAL.QR_CODE_MODAL.MESSAGE_2') }}
          </h3>
        </v-col>
        <v-col cols="12">
          <div class="mt-3 mb-4 qr-code-content text-center">
            <qrcode-vue :value="qrCodeValue" :size="qrCodeSize" level="L" />
          </div>
        </v-col>

        <v-row>
          <v-col cols="12" align="center">
            <v-btn
              :disabled="loading"
              :loading="loading"
              class="boom-button"
              variant="outlined"
              @click="_cancel"
            >
              {{ $t('ATTACHMENT_MODAL.QR_CODE_MODAL.VALIDATION_BUTTON') }}
              <template v-slot:prepend>
                <img alt="send-icon" class="send-white-svg" src="@/assets/icons/file/send.svg" />
              </template>
            </v-btn>
          </v-col>
        </v-row>
      </v-row>

      <div v-if="showAddPcAttachment">
        <div class="mb-5">
          <p data-custom="true" class="mb-5">{{ $t('REPAIR_FILE_ATTACHMENT.MODAL.FILE_NAME') }}</p>
          <v-text-field
            v-model.trim="fileName"
            required
            hide-details
            :rules="requiredRules"
            :maxlength="30"
          />
        </div>
        <p data-custom="true" class="mb-5">{{ $t('REPAIR_FILE_ATTACHMENT.MODAL.FILE') }}</p>
        <v-file-input
          class="logo-selector"
          chips
          counter
          show-size
          required
          accept=".pdf, .jpg, .png"
          v-model="file"
        >
          <template v-slot:append-inner>
            <img
              alt="download.svg"
              class="download-svg mr-4"
              src="@/assets/icons/file/download.svg"
            />
          </template>
        </v-file-input>

        <div class="d-flex justify-center">
          <v-btn
            class="boom-button dialog-button"
            :disabled="!file || !fileName"
            :loading="loading"
            variant="outlined"
            @click="_confirm"
          >
            {{ $t('FILE_STEP_CREATE_FOLDER.MODAL.BUTTON.VALIDATE') }}
          </v-btn>
        </div>
      </div>
    </div>
  </popup-modal>
</template>

<style scoped>
.dialog-button {
  min-width: 100px;
  width: 250px;
}

:deep(.v-selection-control__input > .v-icon) {
  color: #559a1b;
  opacity: unset;
}

:deep(.v-selection-control-group--inline) {
  margin: auto;
}

:deep(.logo-selector .v-field.v-field--appended) {
  justify-content: center !important;
  align-content: center;
}

:deep(.v-selection-control--inline .v-label) {
  opacity: unset;
}

.download-svg {
  height: 20px;
  filter: invert(98%) sepia(0%) saturate(1853%) hue-rotate(181deg) brightness(96%) contrast(80%);
}
</style>
